import { Button, Card, Checkbox, Form, Input, message, Select, Spin, Switch } from "antd";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AppButton from "../common/AppButton";
import { useGoBack } from "../customHooks/useGoBack";
import useWarehouse from "../customHooks/useWarehouse";
import { ClientOrgSelect, SupplierOrgSelect, TimezoneSelect } from "../helpers/common";
import UserService from "../services/User/UserService";

const UserDetails = () => {
    const { data: warehouses = [] } = useWarehouse();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const { initialValues, pageMode, roles, userRole } = location.state || {};
    const goBack = useGoBack();
    const [isActive, setIsActive] = useState(false);
    const supplierRoleId = roles?.find((role) => role.name === "Supplier")?.id;

    useEffect(() => {
        if (pageMode === "edit" && initialValues) {
            form.setFieldsValue({ ...initialValues });
            setIsActive(initialValues.isActive);
        }
    }, [pageMode, initialValues]);

    useEffect(() => {
        if (!warehouses?.length) return;
        if (pageMode === "edit" && initialValues?.preferredWarehouseIDs?.length) return;

        const warehouseIds = warehouses.map(w => w.id);
        form.setFieldsValue({ preferredWarehouseIDs: warehouseIds });
    }, [warehouses, pageMode, initialValues]);

    const handleClose = () => {
        goBack();
    }

    const onFinish = async () => {
        try {
            const values = await form.validateFields();
            const normalizedWarehouseIDs = values.preferredWarehouseIDs || [];
            setLoading(true);

            const payload = initialValues
                ? { ...values, id: initialValues.id, isActive, preferredWarehouseIDs: normalizedWarehouseIDs }
                : { ...values, isActive, preferredWarehouseIDs: normalizedWarehouseIDs };

            const service = initialValues ? UserService.updateUser : UserService.createUser;

            const { status } = await service(payload);
            if (status !== 200) throw new Error();

            message.success("User Saved Successfully");
            goBack();
        } catch (error) {
            console.error("Error saving user:", error);
            message.error(error?.response?.data?.message || "Error saving user. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        setLoading(true);
        UserService.deleteUser(initialValues.id)
            .then((response) => {
                if (response.status === 200) {
                    message.success("User deleted successfully");
                    goBack();
                }
            })
            .catch((error) => {
                console.error("Error deleting user:", error);
                message.error("Error deleting user. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <Form form={form} layout="vertical" className="px-8 relative mb-4" initialValues={{
            detentionFreeDays: 0,
            detentionDailyFee: 0,
        }}>
            <div className="flex justify-start items-center w-full my-2  mx-2 font-inter">
                <span
                    className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10"
                >
                    {pageMode === "edit" ? "User Details" : "New User"}
                </span>
                <div className=" flex items-center justify-end pr-4 pt-2 h-full">
                    <Button
                        type="link"
                        onClick={goBack}
                        className="text-md font-medium !text-[#01A4A6] "
                    >
                        / User
                    </Button>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center items-center h-48">
                    <Spin size="large" />
                </div>
            ) : (<>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 grid grid-cols-1 col-span-2">
                        <div className=" mt-2 grid grid-cols-1 lg:grid-cols-2 gap-y-0 gap-x-16 ml-10 mr-10">
                            <Form.Item
                                name="firstName"
                                label="First Name"
                                rules={[{ required: true, message: "Required" }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Required" }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[{ required: true, type: "email", message: "Invalid email" }]}
                            >
                                <Input disabled={pageMode === "edit"} />
                            </Form.Item>

                            {pageMode === "edit" ? <Form.Item
                                name="isActive"
                                label="Active Status"
                                valuePropName="checked"
                                className="my-auto"
                                layout="horizontal"
                            >
                                <div className="flex justify-end">
                                    <Switch
                                        checked={isActive}
                                        onChange={() => setIsActive(!isActive)}
                                    />
                                </div>
                            </Form.Item> : null}
                        </div>
                    </Card>
                    <div className="col-span-2 flex flex-col justify-between">
                        <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80 col-span-2 h-fit">
                            <div className="grid grid-cols-2 gap-y-0 gap-x-16 ml-10 mr-10">
                                <Form.Item
                                    name="role"
                                    label="Role"
                                    rules={[{ required: true, message: "Please Select Role" }]}
                                    className="col-span-2"
                                >
                                    <Select placeholder="Select Role" disabled={pageMode === "edit" && (userRole !== "ContiAdmin" || initialValues?.role === supplierRoleId)}>
                                        {roles?.map((role) => (
                                            <Select.Option key={role.id} value={role.id}>
                                                {role.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
                                    {({ getFieldValue }) => {
                                        const selectedRoleId = getFieldValue("role") || null;
                                        const hasUserRole = selectedRoleId && roles?.some((r) => selectedRoleId === r.id);
                                        return (pageMode === "edit" && userRole === "ContiAdmin" && hasUserRole) ? (
                                            <Form.Item
                                                name="orgIDs"
                                                label="Org(s)"
                                                rules={[{ required: true, message: "Please Select Org(s)" }]}
                                                className="col-span-2"
                                            >
                                                <ClientOrgSelect mode="multiple" disabled={true} />
                                            </Form.Item>
                                        ) : null;
                                    }}
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
                                    {({ getFieldValue }) => {
                                        const selectedRoleId = getFieldValue("role") || null;
                                        const hasSupplierRole = selectedRoleId && roles?.some((r) => selectedRoleId === r.id && r.name === "Supplier");
                                        return hasSupplierRole ? (
                                            <Form.Item
                                                name="supplierID"
                                                label="Supplier"
                                                rules={[{ required: true, message: "Please Select Supplier" }]}
                                                className="col-span-2"
                                            >
                                                <SupplierOrgSelect form={form} disabled={pageMode === "edit"} />
                                            </Form.Item>
                                        ) : null;
                                    }}
                                </Form.Item>
                                <Form.Item
                                    name="timezone"
                                    label="Timezone"
                                    rules={[{ required: true, message: "Please Select Timezone" }]}
                                    className="col-span-2"
                                >
                                    <TimezoneSelect placeholder="Select Timezone" form={form} showExtra={true} />
                                </Form.Item>
                                <Form.Item
                                    name="preferredWarehouseIDs"
                                    label="Preferred Warehouse(s)"
                                    rules={[{ required: true, message: "Please Select Preferred Warehouse(s)" }]}
                                    className="col-span-2"
                                    extra={<div className="text-sm text-gray-500">
                                        This will set the default warehouse upon login. Users can change it on individual pages if needed.
                                    </div>}
                                >
                                    <Select
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        allowClear
                                        placeholder="Select Warehouse(s)"
                                        optionFilterProp="label"
                                        showSearch
                                        dropdownRender={(menu) => {
                                            const selected = form.getFieldValue("preferredWarehouseIDs") || [];
                                            const warehouseIds = warehouses.map(w => w.id);
                                            const totalCount = warehouseIds.length;
                                            const selectedCount = selected.length;
                                            const allSelected = (selectedCount === totalCount) && (totalCount > 0);
                                            const partiallySelected = (selectedCount > 0) && (selectedCount < totalCount);
                                            return (
                                                <>
                                                    {(totalCount > 1) &&
                                                        <div
                                                            className="px-3 py-1 border-b border-gray-200 flex items-center cursor-pointer"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => form.setFieldsValue({ preferredWarehouseIDs: allSelected ? [] : warehouseIds })}
                                                        >
                                                            <Checkbox
                                                                checked={allSelected}
                                                                indeterminate={partiallySelected}
                                                            />
                                                            <span className="ml-2 font-medium">Select All</span>
                                                        </div>}
                                                    {menu}
                                                </>
                                            );
                                        }}
                                    >
                                        {warehouses.map((w) => (
                                            <Select.Option key={w.id} value={w.id}>
                                                {w.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </div>
                        </Card>
                    </div>
                </div>
                <div className="flex w-full justify-end mt-4">
                    <AppButton variant="save" onClick={onFinish} >Save</AppButton>
                    {/* {pageMode === "edit" ? (<Popconfirm
                        title={
                            <>
                                {`You are about to delete ${form.getFieldValue("companyName")} forwarder.`}
                                <br />
                                Please confirm you wish to proceed.
                            </>
                        }
                        onConfirm={handleDelete}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="primary"
                            className="font-bold ml-2 p-4 py-4 rounded-lg bg-[#789B9E] w-20 shadow-md"
                        >
                            Delete
                        </Button>
                    </Popconfirm>) : null} */}
                    <AppButton variant="close" onClick={handleClose} >Close</AppButton>
                </div>
            </>
            )}
        </Form>
    );
}
export default UserDetails;